import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, User, CheckCircle, Copy, X, Mail, Globe, RefreshCw, FileText } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import AptiviewLoader from '../../../common/AptiviewLoader/AptiviewLoader';
import api from '../../../../api/client';
import { convertMarkdownToHtml } from '../../../../utils/markdownToHTML';
import parse from 'html-react-parser';
import ShowDescriptionDialog from '../../component/ShowDescriptionDialog';

const ScheduledInterviews = ({
    candidateInvites,
    expiredInvites,
    invitesLoading,
    expiredInvitesLoading,
    invitesError,
    expiredInvitesError,
    scheduledInterviewsTab,
    setScheduledInterviewsTab,
    onExpireInvite,
    onResendEmail,
    onViewResults,
    setSnackbar,
    onMultiJDSchedule,
    onRefresh
}) => {
    const { t } = useTranslation();
    const [jdModal, setJdModal] = useState({ open: false, title: '', content: null, loading: false });

    const handleViewJd = async (jdId, defaultTitle) => {
        if (!jdId) return;
        setJdModal({ open: true, title: defaultTitle || 'Job Description', content: null, loading: true });
        try {
            const res = await api.get(`job-descriptions/${jdId}/`);
            const desc = res?.data?.description ?? '';
            setJdModal({
                open: true,
                title: res?.data?.title ?? defaultTitle ?? 'Job Description',
                content: parse(convertMarkdownToHtml(desc)),
                loading: false,
            });
        } catch (err) {
            setJdModal({ open: false, title: '', content: null, loading: false });
            if (setSnackbar) {
                setSnackbar({ open: true, message: err?.response?.data?.detail || 'Failed to load job description', severity: 'error' });
            }
        }
    };
    const renderTabButton = (tabValue, label, emoji) => (
        <button
            type="button"
            onClick={() => setScheduledInterviewsTab(tabValue)}
            className={`px-4 py-2 rounded-lg  font-semibold transition-all duration-200 ${
                scheduledInterviewsTab === tabValue
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-transparent text-gray-600 hover:bg-gray-100'
            }`}
        >
            {emoji} {label}
        </button>
    );

    const renderInviteCard = (invite) => (
        <div
            key={invite.invite_id}
            className="bg-gradient-to-br from-white to-gray-50/50 rounded-xl border border-gray-200/50 p-4 hover:shadow-lg hover:border-blue-300/70 hover:bg-gradient-to-br hover:from-blue-50/30 hover:to-indigo-50/50 hover:scale-105 transition-all duration-300 group cursor-pointer"
        >
            {/* Header */}
            <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-1 break-words">
                    {invite.job_description.title}
                </h4>
                <p className="text-xs text-gray-600 break-words">
                    {invite.job_description.company_name} • {invite.job_description.department_name}
                </p>
            </div>

            {/* Details */}
            <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Calendar size={12} className="flex-shrink-0" />
                    <span>
                        {t('candidateDetails.invited')} {new Date(invite.created_at).toLocaleDateString()}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                    <User size={12} className="flex-shrink-0" />
                    <span className="break-words truncate">
                        {invite.created_by?.email || t('candidateDetails.nA')}
                    </span>
                </div>
                {invite.language && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Globe size={12} className="flex-shrink-0" />
                        <span className="break-words truncate">
                            {invite.language.name || invite.language.code?.toUpperCase() || '—'}
                        </span>
                    </div>
                )}
                {invite.used_at && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                        <CheckCircle size={12} className="flex-shrink-0" />
                        <span>
                            {t('candidateDetails.started')} {new Date(invite.used_at).toLocaleDateString()}
                        </span>
                    </div>
                )}
                {invite.candidate_has_jd_tag && invite.job_description?.id && (
                    <div className="flex items-center gap-2 text-xs">
                        <button
                            type="button"
                            onClick={() => handleViewJd(invite.job_description.id, invite.job_description.title)}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
                            title={t('candidateDetails.jobDescriptionsTooltip')}
                        >
                            <FileText size={12} className="flex-shrink-0" />
                            {t('candidateDetails.viewJdContext')}
                        </button>
                    </div>
                )}
            </div>

            {/* Session Info */}
            {invite.session && (
                <div className="mb-3 p-3 bg-blue-50/50 rounded-lg border border-blue-200/50">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                        {t('candidateDetails.interviewSession')}
                    </h5>
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">
                            {t('candidateDetails.status')}: <span className="text-gray-900 font-medium">{invite.session.status}</span>
                        </span>
                        {invite.session.total_score && (
                            <span className="text-gray-600">
                                {t('candidate.score')}: <span className="text-gray-900 font-medium">{invite.session.total_score}%</span>
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Skills */}
            <div className="mb-4 p-3 bg-purple-50/50 rounded-lg border border-purple-200/50">
                <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    🎯 {t('candidateDetails.selectedSkills')}
                </h5>
                <div className="flex flex-wrap gap-1">
                    {invite.skill_analysis && invite.skill_analysis.selected_skills && invite.skill_analysis.selected_skills.length > 0 ? (
                        <>
                            {invite.skill_analysis.selected_skills.slice(0, 3).map((skill, index) => (
                                <span 
                                    key={index} 
                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200"
                                >
                                    {skill}
                                </span>
                            ))}
                            {invite.skill_analysis.selected_skills.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full border border-gray-200">
                                    +{invite.skill_analysis.selected_skills.length - 3}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-xs text-gray-500 italic px-2 py-1 bg-gray-100 rounded-md border border-gray-200">
                            {t('candidateDetails.noSkillsSelected')}
                        </span>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-1">
                {invite.status === 'active' && (
                    <>
                        <button 
                            onClick={() => {
                                navigator.clipboard.writeText(invite.interview_url);
                                setSnackbar({ open: true, message: t('candidateDetails.urlCopied'), severity: 'success' });
                            }}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-md transition-colors duration-200"
                        >
                            <Copy size={12} />
                            {t('candidateDetails.copyLink')}
                        </button>
                        <button 
                            onClick={() => onResendEmail(invite.invite_id)}
                            className="flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs rounded-md transition-colors duration-200 border border-blue-200 hover:border-blue-300"
                            title={t('candidateDetails.resendEmail')}
                        >
                            <Mail size={12} />
                            {t('candidateDetails.resendEmail')}
                        </button>
                        <button 
                            onClick={() => onExpireInvite(invite.token)}
                            className="flex items-center justify-center gap-1 px-2 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs rounded-md transition-colors duration-200 border border-red-200 hover:border-red-300"
                            title={t('candidateDetails.expire')}
                        >
                            <X size={12} />
                            {t('candidateDetails.expire')}
                        </button>
                    </>
                )}
                {/* {invite.session && (
                    <button 
                        onClick={() => onViewResults(invite.session.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white   rounded-lg transition-colors duration-200"
                    >
                        <Eye size={14} />
                        View Results
                    </button>
                )} */}
            </div>
        </div>
    );

    const renderContent = () => {
        const isLoading = scheduledInterviewsTab === 'active' ? invitesLoading : expiredInvitesLoading;
        const error = scheduledInterviewsTab === 'active' ? invitesError : expiredInvitesError;
        const invites = scheduledInterviewsTab === 'active' ? candidateInvites : expiredInvites;
        
        if (isLoading) {
            return (
                <div className="flex justify-center py-12">
                    <AptiviewLoader
                        size="sm"
                        message={`${t('common.loading')} ${scheduledInterviewsTab === 'active' ? t('candidateDetails.activeInvites').toLowerCase() : t('candidateDetails.expiredInvites').toLowerCase()}...`}
                    />
                </div>
            );
        }
        
        if (error) {
            return (
                <div className="text-center py-12">
                    <p className="text-red-600">{error}</p>
                </div>
            );
        }
        
        if (invites.length === 0) {
            return (
                <div className="text-center py-12">
                    <Calendar size={48} className="mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg  text-gray-500 mb-2">
                        {scheduledInterviewsTab === 'active' 
                            ? t('candidateDetails.noActiveInvites')
                            : t('candidateDetails.noExpiredInvites')
                        }
                    </h3>
                    <p className="text-gray-400">
                        {scheduledInterviewsTab === 'active'
                            ? t('candidate.generateInvite')
                            : t('candidateDetails.expiredInvites')
                        }
                    </p>
                    {scheduledInterviewsTab === 'active' && (
                        <button
                            onClick={onMultiJDSchedule}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white   rounded-lg transition-colors"
                        >
                            {t('candidateDetails.multiJDScheduleBtn')}
                        </button>
                    )}
                </div>
            );
        }
        
        return (
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 ${
                invites.length > 3 ? 'max-h-96 overflow-y-auto pr-2' : ''
            }`}>
                {invites.map(renderInviteCard)}
            </div>
        );
    };

    const handleRefresh = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('candidate.scheduledInterviews')}</h3>
                    <button
                        onClick={onMultiJDSchedule}
                        className="w-6 h-6 inline-flex items-center justify-center rounded-md border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
                        title={t('candidateDetails.multiJDScheduleBtn')}
                    >
                        +
                    </button>
                </div>
                <button
                    onClick={handleRefresh}
                    disabled={scheduledInterviewsTab === 'active' ? invitesLoading : expiredInvitesLoading}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('common.refresh')}
                >
                    <RefreshCw 
                        size={16} 
                        className={scheduledInterviewsTab === 'active' ? (invitesLoading ? 'animate-spin' : '') : (expiredInvitesLoading ? 'animate-spin' : '')} 
                    />
                    {t('common.refresh')}
                </button>
            </div>
            
            {/* Tabs */}
            <div className="flex justify-center mb-6">
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg border border-gray-200">
                    {renderTabButton('active', t('candidateDetails.activeInvites'), '🟢')}
                    {renderTabButton('expired', t('candidateDetails.expiredInvites'), '❌')}
                </div>
            </div>
            
            {renderContent()}

            {/* Bottom CTA: hide when active tab has no invites (empty-state already shows CTA) */}
            {/* {!(scheduledInterviewsTab === 'active' && (candidateInvites?.length || 0) === 0) && (
                <div className="mt-8 flex justify-center">
                    <button
                        onClick={hasAnyInvites ? () => setScheduledInterviewsTab('active') : onMultiJDSchedule}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white   rounded-lg transition-colors"
                    >
                        {hasAnyInvites ? 'Scheduled Interviews' : 'Multi-JD Schedule'}
                    </button>
                </div>
            )} */}

            <ShowDescriptionDialog
                open={jdModal.open}
                onClose={() => setJdModal({ open: false, title: '', content: null, loading: false })}
                content={jdModal.loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <CircularProgress size={36} sx={{ color: '#0ea5e9' }} />
                    </div>
                ) : (
                    jdModal.content
                )}
                title={jdModal.title}
            />
        </div>
    );
};

export default ScheduledInterviews;